import { Link } from 'react-router-dom';
import EventCard from '@/components/feature/EventCard';
import FaqAccordion from '@/components/feature/FaqAccordion';
import { Kicker, SectionHead, ProgressBadge } from '@/components/feature/Atoms';
import { events, columns } from '@/mocks/events';
import { stats } from '@/mocks/site';
import { heroImg, eventImgs } from '@/mocks/images';
import { usePageMeta } from '@/hooks/useSeo';

const colMeta: Record<string, { icon: string; desc: string }> = {
  复盘档案: { icon: 'ri-history-line', desc: '按时间线还原事件的前因后果' },
  反转观察: { icon: 'ri-refresh-line', desc: '追踪实锤与反转的关键节点' },
  回应传真: { icon: 'ri-file-text-line', desc: '逐条拆解各方的官方回应' },
  进展追踪: { icon: 'ri-radar-line', desc: '持续跟进后续发展与澄清' },
  吃瓜法务: { icon: 'ri-shield-check-line', desc: '看清围观需要遵守的边界' },
};

export default function Home() {
  usePageMeta(
    '99吃瓜 - 事件复盘与反转梳理的理性吃瓜媒体',
    '99吃瓜专注互联网事件复盘与反转梳理，用时间线还原前因后果，拆解各方回应，跟进后续进展，帮你把每一场反转看个明白。',
  );

  const flagship = events[0];
  const reversalPicks = [events[1], events[5], events[4]];
  const progressRows = [events[3], events[2], events[6], events[8]];

  return (
    <>
      {/* ===== 首屏 ===== */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-24 top-0 select-none font-heading text-[16rem] font-black leading-none text-background-200/70 md:text-[24rem]">
          瓜
        </div>
        <div className="mx-auto grid max-w-[1280px] items-center gap-10 px-4 py-12 md:grid-cols-[1.05fr_0.95fr] md:px-6 md:py-20">
          <div className="relative z-10">
            <Kicker>理性吃瓜 · 事件复盘媒体</Kicker>
            <h1 className="mt-5 text-4xl font-black leading-[1.15] text-foreground-950 md:text-6xl">
              把每一场反转，
              <br />
              <span className="text-primary-500">看个明白</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-8 text-foreground-600 md:text-lg">
              99吃瓜专注吃瓜事件的事件复盘与反转梳理：用可溯源的时间线还原前因后果，逐条拆解各方回应，持续跟进后续进展，让围观不再被带节奏。
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/event"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                阅读本周深度复盘
                <i className="ri-arrow-right-line ml-2" aria-hidden="true" />
              </Link>
              <Link
                to="/review"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                进入复盘档案
              </Link>
            </div>

            <div className="mt-7 flex flex-wrap gap-2">
              {['事件复盘', '反转梳理', '各方回应', '后续进展'].map((k) => (
                <span
                  key={k}
                  className="inline-flex items-center whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800"
                >
                  {k}
                </span>
              ))}
            </div>
          </div>

          <figure className="relative z-10">
            <div className="overflow-hidden rounded-lg border border-background-200">
              <img
                src={heroImg}
                alt="99吃瓜 用放大镜核查社交网络信息，还原事件前因后果的编辑插画"
                className="h-[340px] w-full object-cover object-top md:h-[440px]"
              />
            </div>
            <figcaption className="absolute -bottom-3 right-4 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-background-50">
              本周热点：已澄清 · 全程复盘完成
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ===== 数据条 ===== */}
      <section className="border-y border-background-200 bg-background-100/70">
        <div className="mx-auto grid max-w-[1280px] grid-cols-2 gap-px overflow-hidden px-4 py-6 md:grid-cols-4 md:px-6">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col items-center px-2 py-3 text-center">
              <span className="font-heading text-2xl font-black text-primary-500 md:text-3xl">{s.value}</span>
              <span className="mt-1 text-xs text-foreground-600 md:text-sm">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== 本周深度复盘 ===== */}
      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHead
            kicker="编辑精选"
            title="本周深度复盘"
            desc="一篇由编辑团队梳理的完整复盘，从首发到澄清全程可追溯。"
            action={
              <Link
                to="/event"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                阅读完整复盘 <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            }
          />

          <Link
            to="/event"
            className="mt-8 grid overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300 lg:grid-cols-2"
          >
            <div className="relative h-64 lg:h-full">
              <img
                src={eventImgs[flagship.slug]}
                alt={flagship.title}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-4 top-4">
                <ProgressBadge label={flagship.progress} />
              </span>
            </div>
            <div className="flex flex-col justify-center p-6 md:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-500">{flagship.kicker}</p>
              <h2 className="mt-3 text-2xl font-black leading-snug text-foreground-950 md:text-3xl">{flagship.title}</h2>
              <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base">{flagship.summary}</p>
              <ul className="mt-5 space-y-2">
                {flagship.briefs.slice(0, 3).map((b) => (
                  <li key={b} className="flex gap-2 text-sm leading-6 text-foreground-700">
                    <i className="mt-1 ri-checkbox-circle-line text-accent-500" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex items-center gap-5 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" aria-hidden="true" />
                  {flagship.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" aria-hidden="true" />
                  阅读 {flagship.readMinutes} 分钟
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-message-3-line" aria-hidden="true" />
                  {flagship.replies.toLocaleString()} 条讨论
                </span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* ===== 反转观察 ===== */}
      <section className="bg-background-100/60 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHead
            kicker="反转观察"
            title="实锤到反转，中间发生了什么"
            desc="一起事件为何会反复横跳？用真实传播规律看清反转梳理的关键节点。"
          />
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {reversalPicks.map((e) => (
              <EventCard key={e.slug} item={e} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 栏目速览 ===== */}
      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHead kicker="栏目" title="按你的吃瓜姿势，选一个入口" />
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {columns.map((c) => {
              const m = colMeta[c];
              return (
                <Link
                  key={c}
                  to={c === '复盘档案' || c === '反转观察' ? '/review' : '/event'}
                  className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-all hover:-translate-y-1 hover:border-primary-300"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                    <i className={m.icon} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 text-base font-bold text-foreground-950">{c}</h3>
                  <p className="mt-2 flex-1 text-xs leading-5 text-foreground-600">{m.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary-600">
                    去看看 <i className="ri-arrow-right-line" aria-hidden="true" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== 进展追踪 ===== */}
      <section className="bg-background-100/60 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHead
            kicker="进展追踪"
            title="这些瓜，仍在跟进"
            desc="后续进展没有终点，我们用「记录、核实、等待、回看」的框架持续跟完每一件事。"
            action={
              <Link
                to="/review"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                全部档案 <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            }
          />
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {progressRows.map((e) => (
              <div
                key={e.slug}
                className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 sm:flex-row sm:items-center"
              >
                <ProgressBadge label={e.progress} />
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-bold text-foreground-950">{e.title}</h3>
                  <p className="mt-1 line-clamp-2 text-sm text-foreground-600">{e.summary}</p>
                </div>
                <Link
                  to="/event"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  查看复盘
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 常见问题 ===== */}
      <section className="py-14 md:py-20">
        <div className="mx-auto grid max-w-[1280px] gap-10 px-4 md:grid-cols-[1fr_0.9fr] md:px-6">
          <div>
            <SectionHead
              kicker="常见问题"
              title="关于吃瓜与复盘，你可能有这些疑问"
              desc="我们把读者最常问的问题集中作答，问题与答案均来自真实内容，欢迎对照阅读。"
            />
            <div className="mt-8">
              <FaqAccordion />
            </div>
          </div>
          <aside className="h-fit lg:sticky lg:top-28">
            <div className="overflow-hidden rounded-lg bg-accent-950 p-7 text-accent-100 md:p-9">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-accent-800 text-xl text-accent-100">
                <i className="ri-quill-pen-line" aria-hidden="true" />
              </span>
              <h3 className="mt-5 font-heading text-2xl font-black leading-snug text-background-50 md:text-3xl">
                吃瓜也要讲证据、讲逻辑、讲边界
              </h3>
              <p className="mt-4 text-sm leading-7 text-accent-200/85">
                我们相信事实先于情绪、完整胜过片段、结论不妨延后。无论热点多热闹，先把信源和时间轴摆出来，再来谈立场。
              </p>
              <ul className="mt-6 space-y-3 text-sm text-accent-200/85">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-primary-400" aria-hidden="true" /> 事实先于情绪
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-primary-400" aria-hidden="true" /> 完整胜过片段
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-primary-400" aria-hidden="true" /> 结论不妨延后
                </li>
              </ul>
              <Link
                to="/about"
                className="mt-7 inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-300 hover:text-primary-200"
              >
                了解我们的编辑原则 <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}